import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { ApiError } from "../api/client";
import {
  type ConversationSummary,
  deleteConversation,
  listConversations,
  shareConversation,
} from "../api/conversations";
import {
  conversationId,
  markConversationPublic,
  openConversation,
  resetConversation,
} from "../state";
import { contactHref } from "../lib/contact";
import { readHistoryCache, writeHistoryCache } from "../lib/historyCache";
import { rememberShareLink, shareLink, shareUrl } from "../lib/share";
import { ConfirmDialog } from "./ConfirmDialog";
import { IconMenu, IconShare, IconTrash } from "./icons";
import { UserMenu } from "./UserMenu";
import { Wordmark } from "./Wordmark";

const PAGE_SIZE = 10;

/** Ignore a touch until it has moved this far, so taps stay taps. */
const DRAG_SLOP = 8;
/** How long a history row must be held to open its share/delete menu. */
const LONG_PRESS_MS = 500;
/** A quick flick past this distance flips the drawer without a full drag. */
const FLICK_DISTANCE = 40;
const FLICK_MS = 300;
/**
 * Elements that own their horizontal gestures (the card popover's color swipe)
 * and therefore win over the drawer's swipe.
 */
const SWIPE_CONFLICT = "[data-swipe-ignore]";

/**
 * Whether the off-canvas sidebar is open. Only meaningful on narrow screens;
 * on wide screens the docked sidebar is driven by `desktopSidebarVisible`.
 */
export const sidebarOpen = signal(false);

/**
 * Whether the docked sidebar is visible on wide screens. Collapsing it takes
 * the drawer out of the layout so the main column re-centers in the viewport.
 * Always starts expanded; the choice is not persisted.
 */
export const desktopSidebarVisible = signal(true);

type HistoryState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; conversations: ConversationSummary[]; hasMore: boolean }
  | { status: "error"; message: string };

/** Hamburger button shown in a view's topbar on narrow screens. */
export function SidebarTrigger() {
  return (
    <button
      type="button"
      class="btn btn--icon btn--ghost sidebar-trigger"
      aria-label="Menu"
      aria-haspopup="menu"
      aria-expanded={sidebarOpen.value}
      aria-controls="app-sidebar"
      onClick={() => {
        sidebarOpen.value = !sidebarOpen.value;
      }}
    >
      <IconMenu />
    </button>
  );
}

/**
 * Desktop counterpart of `SidebarTrigger`: fixed just right of the docked
 * drawer, vertically centered on its head, sliding to a small gap off the
 * viewport's left edge when the drawer is collapsed. Rendered from `Sidebar`
 * (not the views' topbars) so no positioned/filtered header ancestor can
 * capture it.
 */
function SidebarDockToggle() {
  return (
    <button
      type="button"
      class={`btn btn--icon btn--ghost sidebar-dock-trigger${
        desktopSidebarVisible.value ? "" : " is-collapsed"
      }`}
      aria-label="Menu"
      aria-controls="app-sidebar"
      aria-expanded={desktopSidebarVisible.value}
      onClick={() => {
        desktopSidebarVisible.value = !desktopSidebarVisible.value;
      }}
    >
      <IconMenu />
    </button>
  );
}

interface HistoryItemProps {
  conversation: ConversationSummary;
  disabled: boolean;
  onPick: () => void;
  onOpenMenu: (x: number, y: number) => void;
}

/**
 * One history row. A plain click opens it; a long-press (or right-click /
 * keyboard context-menu) opens its actions without navigating. Long-press is
 * resolved on release rather than on a timer, so the menu never appears under
 * the finger mid-press (and a drag past the slop cancels it).
 */
function HistoryItem({
  conversation,
  disabled,
  onPick,
  onOpenMenu,
}: HistoryItemProps) {
  const press = useRef<{ x: number; y: number; at: number } | null>(null);
  const suppressClick = useRef(false);

  function onPointerDown(event: PointerEvent) {
    if (disabled) return;
    // Right-click is handled by `contextmenu`; ignore non-primary buttons.
    if (event.pointerType === "mouse" && event.button !== 0) return;
    press.current = { x: event.clientX, y: event.clientY, at: Date.now() };
    suppressClick.current = false;
  }

  function onPointerMove(event: PointerEvent) {
    const start = press.current;
    if (!start) return;
    if (
      Math.hypot(event.clientX - start.x, event.clientY - start.y) > DRAG_SLOP
    ) {
      press.current = null;
    }
  }

  function onPointerUp(event: PointerEvent) {
    const start = press.current;
    press.current = null;
    if (!start) return;
    const held = Date.now() - start.at >= LONG_PRESS_MS;
    const still =
      Math.hypot(event.clientX - start.x, event.clientY - start.y) <= DRAG_SLOP;
    if (held && still) {
      suppressClick.current = true;
      onOpenMenu(event.clientX, event.clientY);
    }
  }

  return (
    <button
      type="button"
      class="sidebar__item"
      role="menuitem"
      disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        press.current = null;
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onOpenMenu(event.clientX, event.clientY);
      }}
      onClick={() => {
        if (suppressClick.current) {
          suppressClick.current = false;
          return;
        }
        onPick();
      }}
    >
      {conversation.title?.trim() || "Untitled ruling"}
    </button>
  );
}

/**
 * App sidebar: account actions and recent conversations. Docked (and
 * collapsible) beside the main content on wide screens; a full-height
 * off-canvas drawer on narrow ones.
 */
export function Sidebar() {
  const open = sidebarOpen.value;
  const docked = desktopSidebarVisible.value;
  const [history, setHistory] = useState<HistoryState>(() => {
    const cached = readHistoryCache();
    return cached ? { status: "ready", ...cached } : { status: "idle" };
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState("");
  const [busy, setBusy] = useState(false);
  const [, navigate] = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  /** In-flight guard for the auto-loading history pages (state lags renders). */
  const moreBusy = useRef(false);
  /** The history row whose share/delete menu is open, and where to anchor it. */
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(
    null,
  );
  /** A pending confirmation for a row action. */
  const [pending, setPending] = useState<
    {
      action: "share" | "delete";
      conversation: ConversationSummary;
    } | null
  >(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [status, setStatus] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const statusTimer = useRef<number | null>(null);

  // Load the first page once on mount, unless it's already cached.
  useEffect(() => {
    if (history.status !== "idle") return;

    setHistory({ status: "loading" });
    setMoreError("");
    let cancelled = false;
    listConversations({ limit: PAGE_SIZE, offset: 0 })
      .then(({ conversations, hasMore }) => {
        if (cancelled) return;
        writeHistoryCache({ conversations, hasMore });
        setHistory({ status: "ready", conversations, hasMore });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setHistory({
          status: "error",
          message: error instanceof Error
            ? error.message
            : "Couldn't load history.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the drawer on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") sidebarOpen.value = false;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Drag-to-open (swipe right) and drag-to-close (swipe left) share one
  // gesture. Handlers live on document so a swipe anywhere toggles the drawer,
  // which is off-canvas when closed and covered by a backdrop when open. Taps
  // pass through because we only preventDefault once a clear horizontal drag is
  // under way.
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;

    let tracking = false;
    let active = false;
    let decided = false;
    let opening = false;
    let startX = 0;
    let startY = 0;
    let startedAt = 0;
    let width = 0;

    const restore = () => {
      el.style.transition = "";
      el.style.transform = "";
    };

    const onStart = (event: TouchEvent) => {
      // Docked sidebars (wide screens) don't gesture at all.
      if (getComputedStyle(el).position !== "fixed") return;
      const touch = event.touches[0];
      if (!touch) return;
      // Let nested swipe targets handle the gesture themselves.
      if (
        event.target instanceof Element &&
        event.target.closest(SWIPE_CONFLICT)
      ) {
        return;
      }

      // Open: a leftward drag anywhere (including the backdrop) closes it.
      // Closed: a rightward drag opens it.
      opening = !sidebarOpen.value;

      tracking = true;
      active = false;
      decided = false;
      startX = touch.clientX;
      startY = touch.clientY;
      startedAt = Date.now();
      width = el.offsetWidth;
    };

    const onMove = (event: TouchEvent) => {
      if (!tracking) return;
      const touch = event.touches[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (!decided) {
        if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) return;
        decided = true;
        active = opening
          ? dx > 0 && Math.abs(dx) > Math.abs(dy)
          : dx < 0 && Math.abs(dx) > Math.abs(dy);
        if (!active) {
          tracking = false;
          return;
        }
        el.style.transition = "none";
      }

      event.preventDefault();
      const base = opening ? -width : 0;
      const offset = Math.max(-width, Math.min(0, base + dx));
      el.style.transform = `translateX(${offset}px)`;
    };

    const onEnd = (event: TouchEvent) => {
      if (!tracking) return;
      const wasActive = active;
      const wasOpening = opening;
      tracking = false;
      active = false;
      decided = false;
      if (!wasActive) return;

      const touch = event.changedTouches[0];
      const dx = touch ? touch.clientX - startX : 0;
      const elapsed = Date.now() - startedAt;
      const draggedFar = wasOpening ? dx > width / 3 : dx < -width / 3;
      const flicked = wasOpening ? dx > FLICK_DISTANCE : dx < -FLICK_DISTANCE;

      restore();
      if (draggedFar || (flicked && elapsed < FLICK_MS)) {
        sidebarOpen.value = wasOpening;
      }
    };

    const onCancel = () => {
      if (!tracking) return;
      const wasActive = active;
      tracking = false;
      active = false;
      decided = false;
      if (wasActive) restore();
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onCancel);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onCancel);
    };
  }, []);

  async function loadMore() {
    if (history.status !== "ready" || moreBusy.current) return;
    moreBusy.current = true;
    setLoadingMore(true);
    setMoreError("");
    try {
      const { conversations, hasMore } = await listConversations({
        limit: PAGE_SIZE,
        offset: history.conversations.length,
      });
      const next = [...history.conversations, ...conversations];
      writeHistoryCache({ conversations: next, hasMore });
      setHistory({ status: "ready", conversations: next, hasMore });
    } catch (error) {
      setMoreError(
        error instanceof Error ? error.message : "Couldn't load more history.",
      );
    } finally {
      moreBusy.current = false;
      setLoadingMore(false);
    }
  }

  /**
   * Auto-load the next history page once the list is scrolled to the bottom.
   * `fromScroll` retries after a failure (the user nudging the list again);
   * the automatic top-up after each page never does, so errors don't loop.
   */
  function maybeLoadMore(fromScroll: boolean) {
    if (history.status !== "ready" || !history.hasMore || moreBusy.current) {
      return;
    }
    if (moreError && !fromScroll) return;
    const el = bodyRef.current;
    if (!el || el.scrollHeight - el.scrollTop - el.clientHeight > 80) return;
    if (moreError) setMoreError("");
    void loadMore();
  }

  // Keep the list topped up: on mount (or cached restore) and after each page
  // lands, load another page when the content is shorter than the drawer.
  useEffect(() => {
    maybeLoadMore(false);
  }, [history, moreError]);

  function close() {
    sidebarOpen.value = false;
  }

  function askNew() {
    resetConversation();
    close();
    navigate("/ask");
  }

  async function pick(conversation: ConversationSummary) {
    if (busy) return;
    setBusy(true);
    try {
      await openConversation(conversation.id);
      if (conversation.is_public) markConversationPublic(conversation.id);
      close();
      navigate("/chat");
    } catch (error) {
      setHistory({
        status: "error",
        message: error instanceof Error
          ? error.message
          : "Couldn't open that ruling.",
      });
    } finally {
      setBusy(false);
    }
  }

  /** Transient status line under the history list (link copied, errors). */
  function showStatus(message: string) {
    setStatus(message);
    if (statusTimer.current != null) window.clearTimeout(statusTimer.current);
    statusTimer.current = window.setTimeout(() => setStatus(""), 2200);
  }

  // Clear a pending status timer on unmount.
  useEffect(() => {
    return () => {
      if (statusTimer.current != null) window.clearTimeout(statusTimer.current);
    };
  }, []);

  // Dismiss the row menu on an outside press, Escape, or scroll.
  useEffect(() => {
    if (!menu) return;
    const onDown = (event: Event) => {
      if (
        menuRef.current &&
        event.target instanceof Node &&
        menuRef.current.contains(event.target)
      ) {
        return;
      }
      setMenu(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenu(null);
    };
    const onScroll = () => setMenu(null);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    // Make the menu keyboard-reachable when opened via right-click / Menu key.
    menuRef.current?.querySelector("button")?.focus();
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menu]);

  function markPublic(id: string) {
    if (history.status !== "ready") return;
    const conversations = history.conversations.map((item) =>
      item.id === id ? { ...item, is_public: true } : item
    );
    writeHistoryCache({ conversations, hasMore: history.hasMore });
    setHistory({ ...history, conversations });
  }

  function removeFromHistory(id: string) {
    if (history.status !== "ready") return;
    const conversations = history.conversations.filter(
      (item) => item.id !== id,
    );
    writeHistoryCache({ conversations, hasMore: history.hasMore });
    setHistory({ ...history, conversations });
  }

  async function doShare(conversation: ConversationSummary) {
    const id = conversation.id;
    setActionBusy(true);
    try {
      // See `AnswerActions.doShare`: the sheet must open before any await, so
      // publishing (already confirmed by the menu/dialog) runs alongside it.
      const publishing = conversation.is_public
        ? Promise.resolve()
        : shareConversation(id).then((state) => {
          rememberShareLink(id, state.url);
        });

      const result = await shareUrl(shareLink(id));

      try {
        await publishing;
      } catch (error) {
        console.warn("share failed", error);
        showStatus("Couldn't share that ruling.");
        return;
      }
      // The server made it public even if the sheet was dismissed.
      markConversationPublic(id);
      if (!conversation.is_public) markPublic(id);
      if (result === "dismissed") return;
      showStatus(result === "shared" ? "Shared" : "Link copied");
    } finally {
      setActionBusy(false);
    }
  }

  async function doDelete(id: string) {
    setActionBusy(true);
    try {
      await deleteConversation(id);
    } catch (error) {
      // Already gone (e.g. deleted in another tab): treat it as removed.
      if (!(error instanceof ApiError && error.status === 404)) {
        console.warn("delete failed", error);
        showStatus("Couldn't delete that ruling.");
        setActionBusy(false);
        setPending(null);
        return;
      }
    }
    removeFromHistory(id);
    if (conversationId.value === id) {
      resetConversation();
      close();
      navigate("/ask");
    }
    setActionBusy(false);
    setPending(null);
  }

  function requestShare(conversation: ConversationSummary) {
    setMenu(null);
    // Already public: no need to warn again, just hand over the link.
    if (conversation.is_public) {
      void doShare(conversation);
      return;
    }
    setPending({ action: "share", conversation });
  }

  function requestDelete(conversation: ConversationSummary) {
    setMenu(null);
    setPending({ action: "delete", conversation });
  }

  const menuConversation = menu && history.status === "ready"
    ? (history.conversations.find((item) => item.id === menu.id) ?? null)
    : null;
  const menuPos = menu
    ? {
      left: Math.max(8, Math.min(menu.x, window.innerWidth - 188)),
      top: Math.max(8, Math.min(menu.y, window.innerHeight - 104)),
    }
    : null;

  return (
    <>
      {open && <div class="sidebar__backdrop" onClick={close} />}

      <SidebarDockToggle />

      <aside
        id="app-sidebar"
        ref={sidebarRef}
        class={`sidebar${open ? " is-open" : ""}${
          docked ? "" : " is-collapsed"
        }`}
        aria-label="History and account"
      >
        <div class="sidebar__head">
          <Wordmark mark />
        </div>

        {
          /* Re-keyed on reveal (drawer open / docked expand) so the fade-in
            replays each time the drawer comes back into view. */
        }
        <div class="sidebar__new-strip" key={`${open}:${docked}`}>
          <button type="button" class="sidebar__new" onClick={askNew}>
            New Question
          </button>
          <a
            class="sidebar__new sidebar__new--feedback"
            href={contactHref("FAB Judge feedback")}
          >
            Send Feedback
          </a>
        </div>

        <div
          class="sidebar__body"
          ref={bodyRef}
          onScroll={() => maybeLoadMore(true)}
        >
          <div class="sidebar__history">
            <span class="sidebar__label">History</span>

            {history.status === "loading" && (
              <p class="sidebar__note">Loading…</p>
            )}

            {history.status === "error" && (
              <p class="sidebar__note sidebar__note--error" role="alert">
                {history.message}
              </p>
            )}

            {history.status === "ready" &&
              history.conversations.length === 0 && (
              <p class="sidebar__note">No previous questions yet.</p>
            )}

            {history.status === "ready" &&
              history.conversations.map((conversation) => (
                <HistoryItem
                  key={conversation.id}
                  conversation={conversation}
                  disabled={busy}
                  onPick={() => pick(conversation)}
                  onOpenMenu={(x, y) => setMenu({ id: conversation.id, x, y })}
                />
              ))}

            {history.status === "ready" && loadingMore && (
              <p class="sidebar__note">Loading…</p>
            )}

            {history.status === "ready" && moreError && (
              <p class="sidebar__note sidebar__note--error" role="alert">
                {moreError}
              </p>
            )}

            {status && (
              <p class="sidebar__note" role="status">
                {status}
              </p>
            )}
          </div>
        </div>

        <div class="sidebar__foot">
          <UserMenu onNavigate={close} />
        </div>
      </aside>

      {menu && menuConversation && menuPos && (
        <div
          ref={menuRef}
          class="history-menu"
          role="menu"
          style={{ left: `${menuPos.left}px`, top: `${menuPos.top}px` }}
        >
          <button
            type="button"
            class="history-menu__item"
            role="menuitem"
            onClick={() => requestShare(menuConversation)}
          >
            <IconShare />
            Share
          </button>
          <button
            type="button"
            class="history-menu__item history-menu__item--danger"
            role="menuitem"
            onClick={() => requestDelete(menuConversation)}
          >
            <IconTrash />
            Delete
          </button>
        </div>
      )}

      {pending && (
        <ConfirmDialog
          title={pending.action === "delete"
            ? "Delete this ruling?"
            : "Share this ruling?"}
          body={pending.action === "delete"
            ? `"${
              pending.conversation.title?.trim() || "Untitled ruling"
            }" will be removed from your history. This can't be undone.`
            : "This will make the conversation public to anyone with the link."}
          confirmLabel={pending.action === "delete" ? "Delete" : "Share"}
          tone={pending.action === "delete" ? "danger" : "default"}
          busy={actionBusy}
          onCancel={() => {
            if (!actionBusy) setPending(null);
          }}
          onConfirm={() => {
            if (pending.action === "share") {
              setPending(null);
              void doShare(pending.conversation);
            } else {
              void doDelete(pending.conversation.id);
            }
          }}
        />
      )}
    </>
  );
}
